import { useTranslation } from 'react-i18next'

import classNames from 'classnames'
import { ModalPortal } from '../../common/components/Portal/Portal'
import { Button } from '../../common/components/button/Button'
import { CloseButton } from '../../common/components/button/CloseButton'
import { LinkButton } from '../../common/components/button/LinkButton'
import { getIcon } from '../../common/components/icons/utils'
import { useFadeAnimation } from '../../hooks/FadeAnimation/useFadeAnimation'
import { useOnEscPress } from '../../hooks/useOnEscPress'
import { useTrapFocus } from '../../hooks/useTrapFocus'
import { getOldUIRoutes } from '../../routes'
import styles from './LicenseModal.module.css'
import { useCallback } from 'react'
import { useUser } from '../../contexts/userContext'
import { useParams } from 'react-router-dom'
import { getClientInfo } from '../../util/browserSpecifics'

type LicenseModalProps = {
  trialDaysLeft: number
  // TODO: check if we use more than one starting price (different by country?)
  startingPricePerMonth: number
  // TODO: check if we use more than one currency
  currency?: string
  onClickClose: () => void
}

export const LicenseModal: React.FC<LicenseModalProps> = ({
  trialDaysLeft,
  startingPricePerMonth,
  currency,
  onClickClose,
}) => {
  const { t } = useTranslation()
  const modalRef = useTrapFocus<HTMLDivElement>()
  useOnEscPress(onClickClose)
  const { fadeTransition, closeWithDelay } = useFadeAnimation()
  const { user } = useUser()
  const { gameId: gameIdParam } = useParams()

  const onClickContactSupport = useCallback(() => {
    if (!window.Beacon) {
      // Beacon not available; for now just log and close menu
      console.warn('HelpScout Beacon is not available')
      return
    }

    const clientInfo = getClientInfo()

    const identifyData: Record<string, any> = {}

    if (user?.contactName) identifyData.name = user.contactName
    if (user?.email) identifyData.email = user.email
    if (user?.activeBusiness?.id) identifyData['business-id'] = user.activeBusiness.id
    if (user?.activeBusiness?.industry) identifyData.industry = user.activeBusiness.industry
    if (gameIdParam) identifyData['game-id'] = gameIdParam
    if (user?.activeBusiness?.name) identifyData.company = user.activeBusiness.name

    // Always include client info if available
    identifyData['browser-name'] = clientInfo.browser
    identifyData['device-type'] = clientInfo.deviceType
    identifyData.platform = clientInfo.platform
    identifyData['user-agent'] = clientInfo.userAgent

    window.Beacon('identify', identifyData)

    closeWithDelay(350, onClickClose)
    window.Beacon('open')
  }, [
    user?.contactName,
    user?.email,
    user?.activeBusiness?.id,
    user?.activeBusiness?.industry,
    user?.activeBusiness?.name,
    gameIdParam,
    closeWithDelay,
    onClickClose,
  ])

  const onClickCloseInternal = () => {
    closeWithDelay(350, onClickClose)
  }

  return (
    <ModalPortal>
      <div className={styles.backdrop} />
      <div ref={modalRef} className={classNames(styles.container, fadeTransition)}>
        <CloseButton autoFocus className={styles.closeButton} onClick={onClickCloseInternal} />
        <span className={styles.circleWithIcons}>{getIcon('CircleWithIcons')}</span>
        <div className={styles.middleContainer}>
          <h1 className={styles.title}>{t('top_bar.license_modal.title', 'Get your license to stay creating')}</h1>
          <p className={styles.boldText}>
            {t('top_bar.license_modal.expiration.trial_days', {
              count: trialDaysLeft,
              defaultValue: 'In %{count} days you will lose access to creating new games',
            })}
          </p>
          <p className={styles.middleTextSpacing}>{t('top_bar.license_modal.body', '')}</p>
          <p className={styles.boldText}>
            {t('top_bar.license_modal.billing', {
              starting_price_per_month: startingPricePerMonth,
              currency,
              defaultValue: 'Prices starting at %{starting_price_per_month} %{currency} / player / month',
            })}
          </p>
        </div>
        <div className={styles.buttonsContainer}>
          <LinkButton
            label={t('top_bar.license_modal.buttons.choose_license', 'Choose your licence')}
            href={getOldUIRoutes.manageLicence()} /* https://play.seppo.io/shop */
            target='_blank'
            rel='noreferrer'
            labelStyle={{ fontSize: '0.8rem', textTransform: 'uppercase' }}
          />
          <Button variant='outline-tiny' onClick={onClickContactSupport}>
            {t('top_bar.license_modal.buttons.contact_support', 'Contact support')}
          </Button>
          <Button variant='borderless-tiny' style={{ color: 'var(--grey-600)' }} onClick={onClickCloseInternal}>
            {t('top_bar.license_modal.buttons.continue_trial', 'Continue with trial')}
          </Button>
        </div>
      </div>
    </ModalPortal>
  )
}
